'use client';

import { Button } from '@smartproxy/decoro/v2/button';
import { Card } from '@smartproxy/decoro/v2/card';
import { Text } from '@smartproxy/decoro/v2/text';

import { cn } from '@/lib/utils';

import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import { PHTrackerProps } from '@/app/types';
import { phTrackerKey } from '@/integrations/trackingScripts/utils';

import { getBentoCtaLinkProps } from './getBentoCtaLinkProps';

export type BentoProductCardProps = {
  title: string;
  price: string;
  bodyText: RichTextFieldStrapiProps;
  ctaLabel?: string;
  ctaHref?: string;
  ctaTarget?: '_blank';
  illustration?: ImageStrapiProps;
  className?: string;
  onCtaClick?: () => void;
} & PHTrackerProps;

export const BentoProductCard = ({
  title,
  price,
  bodyText,
  ctaLabel,
  ctaHref,
  ctaTarget,
  [phTrackerKey]: phTracker,
  illustration,
  className,
  onCtaClick,
}: BentoProductCardProps) => {
  return (
    <Card
      theme='dark'
      radius='lg'
      className={cn(
        'flex h-full w-full flex-col border-0 bg-gray-dark-800',
        className
      )}
    >
      <div className='relative h-44 w-full shrink-0'>
        {illustration && (
          <ImageStrapi
            block={illustration}
            width={379}
            height={180}
            inner={{
              objectFit: 'cover',
              classNames: {
                container: 'absolute inset-0 min-w-0 h-full w-full',
                image: 'h-full w-full object-cover',
              },
            }}
          />
        )}
        <div className='absolute bottom-2.5 left-3 z-10 flex w-44 flex-col items-start gap-1'>
          <Text
            variant='label_l'
            className='whitespace-nowrap text-gray-dark-400'
          >
            {title}
          </Text>
          <Text
            variant='subtitle'
            weight='medium'
            skin='white'
            lineHeight='var(--leading-xl)'
            className='whitespace-nowrap'
          >
            {price}
          </Text>
        </div>
      </div>

      <div className='flex min-h-36 flex-1 flex-col justify-between p-3 bg-gray-dark-800'>
        <Text variant='body_s' className='text-gray-dark-400' as='div'>
          <RichTextFieldStrapi
            block={bodyText}
            classNames={{
              paragraph: 'm-0 text-inherit',
              listUnordered: 'm-0 ml-0 list-disc gap-0 pl-5 text-inherit',
              listOrdered: 'm-0 ml-0 list-decimal gap-0 pl-5 text-inherit',
            }}
          />
        </Text>

        {ctaLabel && (
          <Button
            {...getBentoCtaLinkProps(ctaHref, ctaTarget, phTracker)}
            variant='outline'
            skin='dark'
            size='small'
            width='100%'
            className='w-full'
            onClick={onCtaClick}
          >
            {ctaLabel}
          </Button>
        )}
      </div>
    </Card>
  );
};
